import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import VimeoPlayer from '@vimeo/player';

import {
  getSortedVideoThumbnails,
  videoIsProcessed,
} from '../../../../../../../util/video';
import PlayButtonIcon from '../../../../../../../components/icons/PlayButtonIcon';
import Button from '../../../../../../../components/global/Button';
import DropdownMenu from '../../../../../../../components/global/DropdownMenu';
import DotTrigger from '../../../../../../../components/global/DotTrigger';

const propTypes = {
  greetingVideo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onRecordVideo: PropTypes.func,
  onDeleteVideo: PropTypes.func,
  isDefaultGreetingVideo: PropTypes.bool,
};

const MemoryBookGreetingVideo = ({
  isOwner,
  greetingVideo,
  onRecordVideo,
  onDeleteVideo,
  isDefaultGreetingVideo,
}) => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoElementRef = useRef(null);
  const vimeoPlayerRef = useRef(null);

  const setupVimeoPlayer = () => {
    vimeoPlayerRef.current = new VimeoPlayer(videoElementRef.current);
    vimeoPlayerRef.current.on('ended', () => {
      setVideoPlaying(false);
    });
    vimeoPlayerRef.current.on('play', () => {
      setVideoPlaying(true);
    });
    vimeoPlayerRef.current.on('pause', () => {
      setVideoPlaying(false);
    });
  };

  useEffect(() => {
    if (videoElementRef?.current) {
      // Create a vimeoPlayerRef:
      setupVimeoPlayer();
    }
  }, [videoElementRef?.current]);

  const videoClasses = classNames({
    'memory-book-share-video-iframe': true,
    'memory-book-share-video-iframe-active': videoPlaying,
  });

  const overlayClasses = classNames({
    'memory-book-share-video-overlay': true,
    'memory-book-share-video-overlay-active': !videoPlaying,
  });

  const isVideoProcessed = videoIsProcessed(greetingVideo);

  if (!isOwner && !isVideoProcessed) {
    return null;
  }

  // Greeting has been recorded but not processed, so neigther
  if (greetingVideo && !isVideoProcessed) {
    return (
      <div className="memory-book-share-no-video">
        <p className="text-center text-extra-bold text-dark">
          Your video message is being processed...
        </p>
        <p className="text-sm text-muted">
          Not to worry &ndash; your invitees will see it when it&apos;s ready!.
        </p>
      </div>
    );
  }

  if (!greetingVideo) {
    return (
      <div className="memory-book-share-no-video">
        <p className="text-center text-extra-bold text-dark mb-1">
          Add a video message with your request for old photos from friends &
          family
        </p>
        <p className="text-sm text-muted">
          Not to worry &ndash; only those you invite will see it.
        </p>
        <Button onClick={onRecordVideo}>
          Add video message with photo request
        </Button>
      </div>
    );
  }

  const thumbnail = getSortedVideoThumbnails(greetingVideo.thumbnails)[
    Math.floor(greetingVideo.thumbnails.length / 2)
  ]?.url;

  return (
    <div className="memory-book-share-video">
      <img src={thumbnail} alt="Greeting Thumb" />
      <iframe
        className={videoClasses}
        title={greetingVideo.name}
        src={`${greetingVideo.embed_url}&inline=1&badge=0&controls=0`}
        allow="autoplay"
        ref={videoElementRef}
      />
      <Button
        btnStyle="reset"
        onClick={() => {
          if (videoPlaying) {
            vimeoPlayerRef.current.pause();
          } else {
            vimeoPlayerRef.current.play();
          }
        }}
        className={overlayClasses}
      >
        <PlayButtonIcon size={60} color="#fff" />
      </Button>
      {!videoPlaying && isOwner && !isDefaultGreetingVideo && (
        <div className="dropdown-menu-dark memory-book-share-video-dropdown">
          <DropdownMenu
            placement="bottom-end"
            toggle={<DotTrigger dotType="vertical" />}
          >
            <Button
              btnStyle="reset"
              className="dropdown-item text-extra-bold text-sm"
              onClick={onRecordVideo}
            >
              Re-record message
            </Button>
            <Button
              btnStyle="reset"
              className="dropdown-item text-danger text-extra-bold text-sm"
              onClick={onDeleteVideo}
            >
              Remove message
            </Button>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
};

MemoryBookGreetingVideo.propTypes = propTypes;

export default MemoryBookGreetingVideo;
